/** Parsed warehouse `tracking` values for quality measure cards. */
export type QualityMeasureTrackingStatus = 'on' | 'off' | 'at-risk' | null;

/**
 * Normalizes the warehouse `tracking` column to a known status.
 * @param tracking - Raw `tracking` value from `customer_measurement_period_metrics`.
 * @returns Parsed status or null when unrecognized.
 */
export const parseQualityMeasureTrackingStatus = (
  tracking: string | null | undefined,
): QualityMeasureTrackingStatus => {
  const normalized = tracking?.trim().toLowerCase();
  if (normalized === 'on') {
    return 'on';
  }
  if (normalized === 'off') {
    return 'off';
  }
  if (normalized === 'at risk') {
    return 'at-risk';
  }
  return null;
};

/**
 * Returns reader-facing copy for a parsed tracking status.
 * @param status - Parsed warehouse tracking status.
 * @returns Phrase describing on/off/at-risk trajectory for the measurement period.
 */
export const qualityMeasureTrackingStatusPhrase = (
  status: QualityMeasureTrackingStatus,
): string => {
  switch (status) {
    case 'on':
      return 'on track for the measurement period';
    case 'off':
      return 'off track for the measurement period';
    case 'at-risk':
      return 'at risk for the measurement period';
    default:
      return 'tracking status unavailable';
  }
};

/** Footnote copy inputs for a quality measure projection card. */
export interface QualityMeasureProjectionFootnote {
  deltaPts: number;
  isAboveTarget: boolean;
  statusPhrase: string;
}

/**
 * Builds footnote copy from projected rate, target, and warehouse tracking.
 * @param projected - Projected completion rate (percent).
 * @param target - Target rate (percent).
 * @param tracking - Warehouse `tracking` column (`On`, `Off`, `At Risk`, etc.).
 * @returns Delta, above/below flag, and status phrase for display.
 */
export const buildQualityMeasureProjectionFootnote = (
  projected: number,
  target: number,
  tracking: string | null | undefined,
): QualityMeasureProjectionFootnote => {
  const deltaPts = Math.round((projected - target) * 10) / 10;
  const isAboveTarget = deltaPts >= 0;
  const parsedStatus = parseQualityMeasureTrackingStatus(tracking);
  const statusPhrase = parsedStatus
    ? qualityMeasureTrackingStatusPhrase(parsedStatus)
    : isAboveTarget
      ? 'above target'
      : 'below target';

  return {
    deltaPts,
    isAboveTarget,
    statusPhrase,
  };
};
